import moment from "moment/min/moment-with-locales";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Chart } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useState } from "react";
import ErrorResponse from "../ErrorResponse";
import { Card, Col } from "react-bootstrap";
import LoadingIndicator from "../LoadingIndicator";
import { mapItemType, mapItemTypeColor } from "../HelperFunctions";
import { useRouteLoaderData } from "react-router-dom";

ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels);

export default function ContentBalanceTrendChart({ data, loading, error, grouping }) {
    const { radios, competitorRadios } = useRouteLoaderData("root");
    const [resolvedRadios, setResolvedRadios] = useState([]);

    useEffect(() => {
        async function resolveRadios() {
            setResolvedRadios((await radios).data.concat((await competitorRadios).data));
        }
        if (radios && competitorRadios) resolveRadios();
    }, [radios, competitorRadios]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        borderRadius: 5,
        pointStyle: "rectRounded",
        plugins: {
            legend: {
                labels: {
                    usePointStyle: true,
                    filter: function (legendItem, data) {
                        // Get the dataset corresponding to the legend item
                        const dataset = data.datasets[legendItem.datasetIndex];

                        // Check if the dataset belongs to Stack 0
                        if (dataset.stack === "Stack 0") {
                            // Get the last item from the dataset label
                            const labelParts = legendItem.text.split(" ");
                            legendItem.text = labelParts[labelParts.length - 1];
                            return true;
                        }
                        // Hide legend items for Stack 1
                        return false;
                    },
                },
            },
            datalabels: {
                color: "white",
                font: {
                    weight: "bold",
                },
                formatter: function (value) {
                    return value > 0 ? `${Math.round(value)}%` : "";
                },
            },
            tooltip: {
                callbacks: {
                    label: function (item) {
                        return item.raw ? `${item.dataset.label}: ${item.raw.toFixed(0)}%` : "";
                    },
                },
            },
        },
        scales: {
            y: {
                ticks: {
                    display: false,
                },
                stacked: true,
                beginAtZero: true,
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
            },
            x: {
                stacked: true,
                grid: {
                    display: false,
                },
            },
        },
        interaction: {
            mode: "index",
        },
    };
    const chartData = useMemo(() => {
        if (data && data.length > 0) {
            const datasets = [];
            const labels = data.map(x => moment(x.date).format(grouping > 2 ? "L" : "LT"));

            for (let ri = 0; ri < data[0].radios.length; ++ri) {
                const currentRadio = data[0].radios[ri];

                for (let iti = 0; iti < currentRadio.balances.length; ++iti) {
                    const balance = currentRadio.balances[iti];

                    datasets.push({
                        type: "bar",
                        label: `${resolvedRadios.find(x => x.radio_id === currentRadio.radio_id)?.radio_name} ${mapItemType(balance.item_type)}`,
                        data: data.map(x => {
                            const foundBalance = x.radios[ri]?.balances.find(b => b.item_type === balance.item_type);
                            return foundBalance ? foundBalance.value : 0;
                        }),
                        backgroundColor: data.map(x => mapItemTypeColor(balance.item_type)),
                        stack: "Stack " + ri,
                    });
                }
            }
            return {
                labels: labels,
                datasets: datasets,
            };
        }
        return null;
    }, [data, resolvedRadios, grouping]);

    return (
        <Card>
            <Card.Header className="text-center">Content Balance Trend</Card.Header>
            <Card.Body className="d-flex flex-column p-1 justify-content-center overflow-auto">
                {loading ? (
                    <LoadingIndicator width={75} />
                ) : error ? (
                    <ErrorResponse errorCode={error} />
                ) : (
                    data && (
                        <Col className="dashboard-chart content-balance">
                            <Chart data={chartData} options={chartOptions} />
                        </Col>
                    )
                )}
            </Card.Body>
        </Card>
    );
}
